import React, { useState } from 'react';
import {
  DollarSign, TrendingUp, MessageSquare, Users,
  Edit2, Save, Plus, Trash2, QrCode, Download, Loader2,
  Heart, CalendarDays, ChevronDown, ChevronUp, Mail, Clock,
  Eye, EyeOff
} from 'lucide-react';
import ShopSummaryWidget from '../../../components/admin/ShopSummaryWidget';
import { formatDate, formatDateTime } from '../../../utils/formatDate';

/* ========== Clickable Stat Card ========== */
const StatCard = ({ label, value, subtext, icon, iconColor, expanded, onClick, children, testId }) => (
  <div className="bg-white border border-[#E5E7EB] rounded-lg overflow-hidden transition-all" data-testid={testId}>
    <button onClick={onClick}
      className="w-full p-5 text-left flex items-center justify-between hover:bg-[#F8F9FA] transition-colors cursor-pointer">
      <div>
        <span className="text-xs text-[#4B5563] block mb-1">{label}</span>
        <p className="text-2xl font-bold text-[#0A0A0A]">{value}</p>
        {subtext && <p className="text-[10px] text-[#BA1826] mt-0.5">{subtext}</p>}
      </div>
      <div className="flex items-center gap-2">
        {icon}
        {expanded ? <ChevronUp className="w-4 h-4 text-[#9CA3AF]" /> : <ChevronDown className="w-4 h-4 text-[#9CA3AF]" />}
      </div>
    </button>
    {expanded && (
      <div className="border-t border-[#E5E7EB] px-5 py-4 bg-[#FAFAFA]">
        {children}
      </div>
    )}
  </div>
);

/* ========== Mini Bar Chart ========== */
const MiniBarChart = ({ data, label }) => {
  if (!data || data.length === 0) return <p className="text-xs text-[#9CA3AF] py-3">No data yet</p>;
  const maxVal = Math.max(...data.map(d => d.value), 1);
  return (
    <div>
      <p className="text-[10px] text-[#4B5563] font-medium mb-2">{label}</p>
      <div className="flex items-end gap-1 h-24">
        {data.map((d, i) => (
          <div key={i} className="flex-1 flex flex-col items-center gap-0.5" title={`${d.label}: ${d.display || d.value}`}>
            <span className="text-[8px] text-[#4B5563] font-medium">{d.display || d.value}</span>
            <div className="w-full rounded-t" style={{
              height: `${Math.max((d.value / maxVal) * 72, 3)}px`,
              backgroundColor: d.value > 0 ? '#BA1826' : '#E5E7EB'
            }} />
            <span className="text-[7px] text-[#9CA3AF] truncate w-full text-center">{d.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

export const DashboardOverviewTab = ({ dashboardData, donations, contacts, subscribers }) => {
  const [expanded, setExpanded] = useState(null); // 'donations' | 'raised' | 'messages' | 'subscribers'

  const toggle = (key) => setExpanded(prev => prev === key ? null : key);

  // Build chart data from donations (last 7 days)
  const buildDonationChart = () => {
    const days = [];
    for (let i = 6; i >= 0; i--) {
      const d = new Date();
      d.setDate(d.getDate() - i);
      const key = d.toISOString().split('T')[0];
      const label = d.toLocaleDateString('en-US', { weekday: 'short' });
      const dayDonations = (donations || []).filter(don => don.created_at && don.created_at.startsWith(key));
      days.push({ label, value: dayDonations.length, display: String(dayDonations.length) });
    }
    return days;
  };

  const buildRevenueChart = () => {
    const days = [];
    for (let i = 6; i >= 0; i--) {
      const d = new Date();
      d.setDate(d.getDate() - i);
      const key = d.toISOString().split('T')[0];
      const label = d.toLocaleDateString('en-US', { weekday: 'short' });
      const dayTotal = (donations || [])
        .filter(don => don.created_at && don.created_at.startsWith(key) && don.status === 'completed')
        .reduce((sum, don) => sum + (don.amount || 0), 0);
      days.push({ label, value: dayTotal, display: `$${dayTotal}` });
    }
    return days;
  };

  return (
  <div className="space-y-6">
    {/* Stats Grid — Clickable */}
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="dashboard-stats-grid">
      <StatCard
        label="Total Donations"
        value={dashboardData.total_donations}
        icon={<DollarSign className="w-5 h-5 text-[#BA1826]" />}
        expanded={expanded === 'donations'}
        onClick={() => toggle('donations')}
        testId="stat-card-donations"
      >
        <MiniBarChart data={buildDonationChart()} label="Donations (Last 7 Days)" />
        <div className="mt-3 max-h-48 overflow-y-auto space-y-2">
          <p className="text-[10px] font-semibold text-[#4B5563] mb-1">All Donations</p>
          {(donations || []).length === 0 ? (
            <p className="text-xs text-[#9CA3AF]">No donations yet</p>
          ) : (
            (donations || []).slice(0, 20).map((d, i) => (
              <div key={i} className="flex items-center justify-between py-1.5 border-b border-[#F1F5F9] last:border-0 text-xs">
                <div>
                  <span className="font-medium text-[#0A0A0A]">{d.donor_name || 'Anonymous'}</span>
                  <span className="text-[#9CA3AF] ml-2">{d.donor_email || ''}</span>
                </div>
                <span className={`font-semibold ${d.status === 'completed' ? 'text-green-600' : 'text-amber-600'}`}>${d.amount}</span>
              </div>
            ))
          )}
        </div>
      </StatCard>

      <StatCard
        label="Amount Raised"
        value={`$${dashboardData.total_amount.toLocaleString()}`}
        icon={<TrendingUp className="w-5 h-5 text-green-500" />}
        expanded={expanded === 'raised'}
        onClick={() => toggle('raised')}
        testId="stat-card-raised"
      >
        <MiniBarChart data={buildRevenueChart()} label="Revenue (Last 7 Days)" />
        <div className="mt-3 flex items-center justify-between text-xs">
          <span className="text-[#4B5563]">Average donation:</span>
          <span className="font-semibold text-[#0A0A0A]">
            ${dashboardData.total_donations > 0 ? (dashboardData.total_amount / dashboardData.total_donations).toFixed(2) : '0'}
          </span>
        </div>
      </StatCard>

      <StatCard
        label="Contact Messages"
        value={dashboardData.contact_submissions}
        subtext={dashboardData.unread_contacts > 0 ? `${dashboardData.unread_contacts} unread` : null}
        icon={<MessageSquare className="w-5 h-5 text-blue-500" />}
        expanded={expanded === 'messages'}
        onClick={() => toggle('messages')}
        testId="stat-card-messages"
      >
        <div className="max-h-64 overflow-y-auto space-y-3">
          {(contacts || []).length === 0 ? (
            <p className="text-xs text-[#9CA3AF]">No messages yet</p>
          ) : (
            (contacts || []).map((c, i) => (
              <div key={i} className="bg-white rounded-lg p-3 border border-[#E5E7EB]" data-testid={`message-${i}`}>
                <div className="flex items-center justify-between mb-1.5">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-semibold text-[#0A0A0A]">{c.name}</span>
                    {c.status === 'unread' && <span className="w-2 h-2 bg-[#BA1826] rounded-full" />}
                  </div>
                  <span className="text-[9px] text-[#9CA3AF] flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    {c.created_at ? formatDate(c.created_at) : ''}
                  </span>
                </div>
                <p className="text-[10px] font-medium text-[#4B5563] mb-0.5">{c.subject || 'No subject'}</p>
                <p className="text-[10px] text-[#6B7280] leading-relaxed">{c.message || ''}</p>
                <div className="flex items-center gap-2 mt-2 text-[9px] text-[#9CA3AF]">
                  <Mail className="w-3 h-3" /> {c.email || ''}
                </div>
              </div>
            ))
          )}
        </div>
      </StatCard>

      <StatCard
        label="Subscribers"
        value={dashboardData.newsletter_subscribers}
        icon={<Users className="w-5 h-5 text-purple-500" />}
        expanded={expanded === 'subscribers'}
        onClick={() => toggle('subscribers')}
        testId="stat-card-subscribers"
      >
        <div className="max-h-64 overflow-y-auto">
          {(!subscribers || subscribers.length === 0) ? (
            <p className="text-xs text-[#9CA3AF]">No subscribers yet</p>
          ) : (
            <div className="space-y-1">
              {subscribers.map((s, i) => (
                <div key={i} className="flex items-center justify-between py-2 border-b border-[#F1F5F9] last:border-0 text-xs">
                  <div className="flex items-center gap-2">
                    <div className="w-6 h-6 rounded-full bg-purple-100 flex items-center justify-center text-[10px] font-bold text-purple-600">
                      {(s.email || s.name || '?')[0].toUpperCase()}
                    </div>
                    <span className="font-medium text-[#0A0A0A]">{s.name || s.email}</span>
                  </div>
                  <span className="text-[9px] text-[#9CA3AF]">
                    {s.created_at ? formatDate(s.created_at) : ''}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </StatCard>
    </div>

    {/* Fundraising Progress */}
    {dashboardData.fundraising_goal && (
      <div className="bg-white p-6 border border-[#E5E7EB] rounded-lg">
        <h3 className="font-heading font-semibold mb-4">Fundraising Progress</h3>
        <div className="relative h-4 bg-[#F1F5F9] rounded-full overflow-hidden mb-2">
          <div
            className="absolute inset-y-0 left-0 bg-[#BA1826] rounded-full"
            style={{ width: `${Math.min((dashboardData.fundraising_goal.current_amount / dashboardData.fundraising_goal.goal_amount) * 100, 100)}%` }}
          ></div>
        </div>
        <div className="flex justify-between text-sm">
          <span className="text-[#BA1826] font-semibold">${dashboardData.fundraising_goal.current_amount.toLocaleString()}</span>
          <span className="text-[#4B5563]">Goal: ${dashboardData.fundraising_goal.goal_amount.toLocaleString()}</span>
        </div>
      </div>
    )}

    {/* Shop Summary — quick at-a-glance merch revenue + new-orders badge */}
    <ShopSummaryWidget onOpenShop={() => {
      const btn = document.querySelector('[data-testid="admin-shop-tab"]');
      if (btn) btn.click();
    }} />

    {/* Recent Activity - now inside expandable cards above */}
  </div>
  );
};

export const DonationsTab = ({ donations }) => (
  <div className="bg-white border border-[#E5E7EB] rounded-lg overflow-hidden">
    {donations.length === 0 ? (
      <div className="p-8 text-center">
        <DollarSign className="w-10 h-10 text-[#E5E7EB] mx-auto mb-3" />
        <p className="text-sm text-[#94A3B8]" data-testid="no-donations-table-msg">No donations yet</p>
      </div>
    ) : (
    <div className="overflow-x-auto">
      <table className="w-full">
        <thead className="bg-[#F8F9FA]">
          <tr>
            <th className="px-4 py-3 text-left text-xs font-semibold text-[#4B5563]">Date</th>
            <th className="px-4 py-3 text-left text-xs font-semibold text-[#4B5563]">Donor</th>
            <th className="px-4 py-3 text-left text-xs font-semibold text-[#4B5563]">Amount</th>
            <th className="px-4 py-3 text-left text-xs font-semibold text-[#4B5563]">Package</th>
            <th className="px-4 py-3 text-left text-xs font-semibold text-[#4B5563]">Status</th>
          </tr>
        </thead>
        <tbody>
          {donations.map((donation) => (
            <tr key={donation.id} className="border-t border-[#E5E7EB]">
              <td className="px-4 py-3 text-sm" title={formatDateTime(donation.created_at)}>{formatDate(donation.created_at)}</td>
              <td className="px-4 py-3 text-sm">
                <div>
                  <p className="font-medium">{donation.donor_name || 'Anonymous'}</p>
                  <p className="text-xs text-[#4B5563]">{donation.donor_email || '-'}</p>
                </div>
              </td>
              <td className="px-4 py-3 text-sm font-semibold">${donation.amount}</td>
              <td className="px-4 py-3 text-sm">{donation.package_type}</td>
              <td className="px-4 py-3">
                <span className={`text-xs px-2 py-1 rounded ${
                  donation.status === 'completed' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
                }`}>
                  {donation.status}
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
    )}
  </div>
);

export const GoalsTab = ({ ctx }) => {
  const { dashboardData, editingGoal, setEditingGoal, goalForm, setGoalForm, updateGoal } = ctx;
  const goal = dashboardData?.fundraising_goal;
  if (!goal) return null;

  return (
    <div className="bg-white p-6 border border-[#E5E7EB] rounded-lg max-w-xl">
      <h3 className="font-heading font-semibold mb-6">Fundraising Goal Settings</h3>
      {editingGoal ? (
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-[#4B5563] mb-1">Goal Amount ($)</label>
            <input type="number" value={goalForm.goal_amount}
              onChange={(e) => setGoalForm({ ...goalForm, goal_amount: parseFloat(e.target.value) })}
              className="w-full px-4 py-2 border border-[#E5E7EB] rounded-sm focus:border-[#BA1826]" />
          </div>
          <div>
            <label className="block text-sm font-medium text-[#4B5563] mb-1">Title</label>
            <input type="text" value={goalForm.title}
              onChange={(e) => setGoalForm({ ...goalForm, title: e.target.value })}
              className="w-full px-4 py-2 border border-[#E5E7EB] rounded-sm focus:border-[#BA1826]" />
          </div>
          <div>
            <label className="block text-sm font-medium text-[#4B5563] mb-1">Description</label>
            <textarea value={goalForm.description}
              onChange={(e) => setGoalForm({ ...goalForm, description: e.target.value })}
              className="w-full px-4 py-2 border border-[#E5E7EB] rounded-sm focus:border-[#BA1826]" rows={3} />
          </div>
          <div className="flex gap-2">
            <button onClick={updateGoal} className="btn-primary"><Save className="w-4 h-4" /> Save</button>
            <button onClick={() => setEditingGoal(false)} className="btn-secondary">Cancel</button>
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          <div className="flex justify-between py-2 border-b border-[#E5E7EB]">
            <span className="text-[#4B5563]">Goal Amount</span>
            <span className="font-semibold">${goal.goal_amount.toLocaleString()}</span>
          </div>
          <div className="flex justify-between py-2 border-b border-[#E5E7EB]">
            <span className="text-[#4B5563]">Current Amount</span>
            <span className="font-semibold text-[#BA1826]">${goal.current_amount.toLocaleString()}</span>
          </div>
          <div className="flex justify-between py-2 border-b border-[#E5E7EB]">
            <span className="text-[#4B5563]">Progress</span>
            <span className="font-semibold">{((goal.current_amount / goal.goal_amount) * 100).toFixed(1)}%</span>
          </div>
          <button onClick={() => setEditingGoal(true)} className="btn-primary">
            <Edit2 className="w-4 h-4" /> Edit Goal
          </button>
        </div>
      )}
    </div>
  );
};

export const ContactsTab = ({ contacts, markContactRead }) => (
  <div className="space-y-4">
    {contacts.length === 0 ? (
      <div className="bg-white p-8 border border-[#E5E7EB] rounded-lg text-center">
        <MessageSquare className="w-10 h-10 text-[#E5E7EB] mx-auto mb-3" />
        <p className="text-sm text-[#94A3B8]" data-testid="no-contacts-msg">No messages yet</p>
      </div>
    ) : (
    contacts.map((contact) => (
      <div key={contact.id} className={`bg-white p-6 border rounded-lg ${contact.status === 'unread' ? 'border-[#BA1826]' : 'border-[#E5E7EB]'}`}>
        <div className="flex items-start justify-between mb-4">
          <div>
            <h4 className="font-semibold text-[#0A0A0A] flex items-center gap-2">
              {contact.name}
              {contact.status === 'unread' && <span className="w-2 h-2 bg-[#BA1826] rounded-full"></span>}
            </h4>
            <p className="text-sm text-[#4B5563]">{contact.email}</p>
          </div>
          <span className="text-xs text-[#4B5563]">{formatDateTime(contact.created_at)}</span>
        </div>
        <p className="text-sm font-medium text-[#0A0A0A] mb-2">{contact.subject}</p>
        <p className="text-sm text-[#4B5563] mb-4">{contact.message}</p>
        {contact.status === 'unread' && (
          <button onClick={() => markContactRead(contact.id)} className="text-sm text-[#BA1826] hover:underline">
            Mark as read
          </button>
        )}
      </div>
    ))
    )}
  </div>
);

export const SubscribersTab = ({ subscribers }) => (
  <div className="bg-white border border-[#E5E7EB] rounded-lg overflow-hidden">
    {subscribers.length === 0 ? (
      <div className="p-8 text-center">
        <Users className="w-10 h-10 text-[#E5E7EB] mx-auto mb-3" />
        <p className="text-sm text-[#94A3B8]" data-testid="no-subscribers-msg">No subscribers yet</p>
      </div>
    ) : (
      <table className="w-full">
        <thead className="bg-[#F8F9FA]">
          <tr>
            <th className="px-4 py-3 text-left text-xs font-semibold text-[#4B5563]">Email</th>
            <th className="px-4 py-3 text-left text-xs font-semibold text-[#4B5563]">Subscribed Date</th>
            <th className="px-4 py-3 text-left text-xs font-semibold text-[#4B5563]">Status</th>
          </tr>
        </thead>
        <tbody>
          {subscribers.map((sub) => (
            <tr key={sub.id} className="border-t border-[#E5E7EB]">
              <td className="px-4 py-3 text-sm">{sub.email}</td>
              <td className="px-4 py-3 text-sm">{formatDate(sub.subscribed_at)}</td>
              <td className="px-4 py-3">
                <span className="text-xs px-2 py-1 rounded bg-green-100 text-green-700">{sub.status}</span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    )}
  </div>
);

export const CountriesTab = ({ ctx }) => {
  const { countries, countryForm, setCountryForm, addCountry, deleteCountry } = ctx;

  return (
    <div className="space-y-6">
      <div className="bg-white p-6 border border-[#E5E7EB] rounded-lg">
        <h3 className="font-heading font-semibold mb-4">Add New Country/Region</h3>
        <div className="grid md:grid-cols-2 gap-4">
          <input type="text" placeholder="Country Name" value={countryForm.name}
            onChange={(e) => setCountryForm({ ...countryForm, name: e.target.value })}
            className="px-4 py-2 border border-[#E5E7EB] rounded-sm focus:border-[#BA1826]" />
          <select value={countryForm.region}
            onChange={(e) => setCountryForm({ ...countryForm, region: e.target.value })}
            className="px-4 py-2 border border-[#E5E7EB] rounded-sm focus:border-[#BA1826]">
            <option value="">Select Region</option>
            <option value="Caribbean">Caribbean</option>
            <option value="Central America">Central America</option>
            <option value="South America">South America</option>
            <option value="North America">North America</option>
            <option value="Africa">Africa</option>
            <option value="Asia">Asia</option>
            <option value="Europe">Europe</option>
          </select>
          <textarea placeholder="Description" value={countryForm.description}
            onChange={(e) => setCountryForm({ ...countryForm, description: e.target.value })}
            className="px-4 py-2 border border-[#E5E7EB] rounded-sm focus:border-[#BA1826] md:col-span-2" rows={2} />
          <select value={countryForm.status}
            onChange={(e) => setCountryForm({ ...countryForm, status: e.target.value })}
            className="px-4 py-2 border border-[#E5E7EB] rounded-sm focus:border-[#BA1826]">
            <option value="planned">Planned</option>
            <option value="active">Active</option>
          </select>
          <label className="flex items-center gap-2">
            <input type="checkbox" checked={countryForm.is_primary}
              onChange={(e) => setCountryForm({ ...countryForm, is_primary: e.target.checked })}
              className="w-4 h-4 text-[#BA1826]" />
            <span className="text-sm">Primary Focus</span>
          </label>
        </div>
        <button onClick={addCountry} className="btn-primary mt-4">
          <Plus className="w-4 h-4" /> Add Country
        </button>
      </div>

      <div className="bg-white border border-[#E5E7EB] rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-[#F8F9FA]">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-[#4B5563]">Country</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-[#4B5563]">Region</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-[#4B5563]">Status</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-[#4B5563]">Primary</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-[#4B5563]">Actions</th>
              </tr>
            </thead>
            <tbody>
              {countries.map((country) => (
                <tr key={country.id} className="border-t border-[#E5E7EB]">
                  <td className="px-4 py-3 text-sm font-medium">{country.name}</td>
                  <td className="px-4 py-3 text-sm">{country.region}</td>
                  <td className="px-4 py-3">
                    <span className={`text-xs px-2 py-1 rounded ${
                      country.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
                    }`}>{country.status}</span>
                  </td>
                  <td className="px-4 py-3">
                    {country.is_primary && <span className="text-xs px-2 py-1 rounded bg-[#FEE2E2] text-[#BA1826]">Primary</span>}
                  </td>
                  <td className="px-4 py-3">
                    <button onClick={() => deleteCountry(country.id)} className="text-red-500 hover:text-red-700">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export const QRCodeTab = ({ ctx }) => {
  const { donateQr, eventsQr, qrLoading, fetchQrCode, downloadQrCode } = ctx;

  const QRCard = ({ title, description, qrData, type, icon }) => (
    <div className="bg-white p-6 border border-[#E5E7EB] rounded-lg text-center">
      <div className="flex items-center justify-center gap-2 mb-2">
        {icon}
        <h3 className="font-heading text-lg font-bold" data-testid={`qr-title-${type}`}>{title}</h3>
      </div>
      <p className="text-xs text-[#4B5563] mb-5">{description}</p>

      {!qrData && !qrLoading && (
        <button onClick={() => fetchQrCode(type)} className="btn-primary mx-auto" data-testid={`generate-qr-${type}-btn`}>
          Generate QR Code
        </button>
      )}

      {qrLoading === type && (
        <div className="flex flex-col items-center gap-3 py-6">
          <Loader2 className="w-6 h-6 animate-spin text-[#BA1826]" />
          <p className="text-xs text-[#4B5563]">Generating...</p>
        </div>
      )}

      {qrData && (
        <div className="space-y-4">
          <div className="bg-[#F8F9FA] p-4 rounded-lg inline-block" data-testid={`qr-preview-${type}`}>
            <img src={qrData.image} alt={`${title} QR Code`}
              className="w-48 h-48 mx-auto" data-testid={`qr-image-${type}`} />
          </div>
          <div>
            <p className="text-xs text-[#4B5563] mb-0.5">Links to:</p>
            <p className="text-sm font-medium text-[#BA1826] break-all" data-testid={`qr-url-${type}`}>
              {qrData.site_url}
            </p>
          </div>
          <div className="flex flex-col gap-2">
            <button onClick={() => downloadQrCode(type)} className="btn-primary text-sm" data-testid={`download-qr-${type}-btn`}>
              <Download className="w-4 h-4" /> Download
            </button>
            <button onClick={() => fetchQrCode(type)} className="btn-secondary text-xs" data-testid={`regenerate-qr-${type}-btn`}>
              Regenerate
            </button>
          </div>
        </div>
      )}
    </div>
  );

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      <div className="text-center mb-4">
        <QrCode className="w-10 h-10 text-[#BA1826] mx-auto mb-3" />
        <h2 className="font-heading text-xl font-bold">QR Codes</h2>
        <p className="text-sm text-[#4B5563] mt-1">
          Generate and download branded QR codes for your donation page and events page.
        </p>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <QRCard
          title="Donation QR"
          description="Directs to your donation page — perfect for flyers, business cards, and social media."
          qrData={donateQr}
          type="donate"
          icon={<Heart className="w-5 h-5 text-[#BA1826]" />}
        />
        <QRCard
          title="Events & Fundraisers QR"
          description="Directs to your events page — share at fundraisers for RSVPs and event details."
          qrData={eventsQr}
          type="events"
          icon={<CalendarDays className="w-5 h-5 text-[#BA1826]" />}
        />
      </div>

      <div className="p-4 bg-[#FEF2F2] border border-[#FECACA] rounded-lg">
        <h4 className="text-sm font-semibold text-[#0A0A0A] mb-2">Usage Tips</h4>
        <ul className="text-xs text-[#4B5563] space-y-1">
          <li>- Print at minimum 1 inch (2.5cm) for reliable scanning</li>
          <li>- The heart logo in the center is your brand identifier</li>
          <li>- Works on flyers, posters, business cards, and social media</li>
          <li>- Red border matches the Novena Foundation brand colors</li>
          <li>- Both QR codes link to novenahf.org and will always work</li>
        </ul>
      </div>
    </div>
  );
};
